import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Clock, Euro, GraduationCap, Users, Calendar, ExternalLink, Facebook, Instagram, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PLANITY_URL = 'https://www.planity.com/hilanta-13004-marseille';
const FACEBOOK_URL = 'https://www.facebook.com/hilanta.marseille';
const INSTAGRAM_URL = 'https://www.instagram.com/espace_hilanta/';

// Couleurs par catégorie comme dans le flyer Hilanta
const CATEGORY_COLORS = {
  energetique: {
    title: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    price: 'text-emerald-700'
  },
  signature: {
    title: 'text-terracotta',
    bg: 'bg-terracotta-50',
    border: 'border-terracotta-200',
    price: 'text-terracotta'
  },
  reflexologie: {
    title: 'text-[#7B5E3B]',
    bg: 'bg-[#F4ECDE]',
    border: 'border-[#D8C6A3]',
    price: 'text-[#7B5E3B]'
  },
  feminin: {
    title: 'text-[#4A6D5C]',
    bg: 'bg-[#4A6D5C]/10',
    border: 'border-[#4A6D5C]/30',
    price: 'text-[#4A6D5C]'
  },
  enfants: {
    title: 'text-rose-700',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    price: 'text-rose-700'
  },
  naturopathie: {
    title: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    price: 'text-amber-700'
  },
  // Legacy for backward compatibility
  massage: {
    title: 'text-terracotta',
    bg: 'bg-terracotta-50',
    border: 'border-terracotta-200',
    price: 'text-terracotta'
  },
  activite: {
    title: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    price: 'text-blue-700'
  }
};

const FLYER_ORDER = ['energetique', 'signature', 'reflexologie', 'feminin', 'enfants', 'naturopathie'];
const CATEGORY_META = {
  energetique:  { title: 'Soins Énergétiques' },
  signature:    { title: 'Soins Signature Bien-être', subtitle: 'Option DUO disponible (prix x2)' },
  reflexologie: { title: 'Réflexologie' },
  feminin:      { title: 'Bien-être Féminin' },
  enfants:      { title: 'Bien-être Enfant' },
  naturopathie: { title: 'Conseils en Naturopathie', subtitle: '(SUR RENDEZ-VOUS)' },
};

// Service Item Component
const ServiceItem = ({ service, colors }) => {
  const durationInName = service.name.includes('min');
  
  return (
    <div className={`p-4 sm:p-5 rounded-xl ${colors.bg} border-2 ${colors.border} mb-4`}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
        <div className="flex-1">
          <h3 className={`font-heading text-lg sm:text-xl ${colors.title} font-semibold`}>
            {service.name}
          </h3>
          <p className="font-body text-sm text-warm-brown-light mt-2 leading-relaxed">
            {service.description}
          </p>
        </div>
        <div className="flex items-center gap-3 sm:flex-col sm:items-end">
          <span className={`font-heading text-xl sm:text-2xl ${colors.price} font-bold`}>
            {service.price}€
          </span>
          {!durationInName && service.duration > 0 && (
            <span className="flex items-center gap-1 text-warm-brown-light text-sm">
              <Clock size={14} />
              {service.duration} min
            </span>
          )}
        </div>
      </div>
      {/* DUO info for massages */}
      {(service.category === 'massage' || service.name.toLowerCase().includes('femme enceinte')) && 
       !service.name.toLowerCase().includes('duo') && (
        <div className="mt-3 pt-3 border-t border-gray-200/50">
          <p className="text-xs text-terracotta flex items-center gap-1">
            <Users size={14} />
            Option DUO disponible (prix x2 pour 2 personnes)
          </p>
        </div>
      )}
    </div>
  );
};

// Grouped Service Item Component (for Silhouette variants)
const GroupedServiceItem = ({ groupName, services, colors }) => {
  const description = services[0]?.description || '';
  const sortedServices = [...services].sort((a, b) => (a.duration || 0) - (b.duration || 0));
  
  return (
    <div className={`p-4 sm:p-5 rounded-xl ${colors.bg} border-2 ${colors.border} mb-4`}>
      <h3 className={`font-heading text-lg sm:text-xl ${colors.title} font-semibold mb-2`}>
        {groupName}
      </h3>
      <p className="font-body text-sm text-warm-brown-light mb-4 leading-relaxed">
        {description}
      </p>
      
      {/* Duration options */}
      <div className="flex flex-wrap gap-3">
        {sortedServices.map(service => (
          <div 
            key={service.id} 
            className="bg-white rounded-lg px-4 py-2 border border-gray-200"
          >
            <span className="flex items-center gap-2">
              <Clock size={14} className={colors.title} />
              <span className="font-medium">{service.duration} min</span>
              <span className={`${colors.price} font-bold`}>{service.price}€</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ServicesSimple = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // Group services by category, sorted by group_order then duration
  const servicesByCategory = FLYER_ORDER.reduce((acc, cat) => {
    const items = services
      .filter(s => s.category === cat)
      .sort((a, b) => {
        const ga = a.group_order ?? 99;
        const gb = b.group_order ?? 99;
        if (ga !== gb) return ga - gb;
        return (a.duration ?? 0) - (b.duration ?? 0);
      });
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-terracotta border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" data-testid="services-page">
      <title>Nos Prestations | Hilanta - Massages, Naturopathie, Bien-être</title>
      <meta name="description" content="Découvrez toutes nos prestations : massage signature, réflexologie plantaire, Chi Nei Tsang, massage femme enceinte, naturopathie. Tarifs et réservation sur Planity." />
      
      {/* Header */}
      <section className="py-8 sm:py-12 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-body text-xs sm:text-sm uppercase tracking-widest text-terracotta">Nos Prestations</span>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-warm-brown mt-2 mb-4">
            Nos Prestations
          </h1>
          <p className="font-body text-sm sm:text-base text-warm-brown-light max-w-2xl mx-auto px-4">
            Découvrez nos soins de bien-être et réservez sur Planity
          </p>
        </div>
      </section>

      {/* Tarifs spéciaux banner */}
      <section className="py-4 bg-terracotta">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8 text-white">
            <div className="flex items-center gap-2">
              <GraduationCap size={20} />
              <span className="font-body text-sm sm:text-base">Étudiants : <strong>-10%</strong></span>
            </div>
            <span className="font-body text-xs sm:text-sm opacity-90">Sur présentation de la carte étudiant</span>
          </div>
        </div>
      </section>

      {/* Services Grid — 6 categories matching the Hilanta flyer */}
      <section className="py-8 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
            {FLYER_ORDER.map((cat) => {
              const items = servicesByCategory[cat];
              if (!items || items.length === 0) return null;
              const colors = CATEGORY_COLORS[cat];
              const meta = CATEGORY_META[cat];
              // Group items that share the same `group`
              const seenGroups = new Set();
              return (
                <div key={cat} data-testid={`services-category-${cat}`}>
                  <h2 className={`font-heading text-xl sm:text-2xl ${colors.title} font-bold mb-2 uppercase tracking-wide text-center`}>
                    {meta.title}
                  </h2>
                  {meta.subtitle && (
                    <p className={`font-body text-xs sm:text-sm ${colors.title} opacity-80 text-center mb-4`}>
                      {meta.subtitle}
                    </p>
                  )}
                  {!meta.subtitle && <div className="mb-4" />}
                  {items.map((service) => {
                    if (service.group) {
                      if (seenGroups.has(service.group)) return null;
                      seenGroups.add(service.group);
                      const variants = items.filter(i => i.group === service.group);
                      if (variants.length > 1) {
                        return (
                          <GroupedServiceItem
                            key={`group-${cat}-${service.group}`}
                            groupName={service.group}
                            services={variants}
                            colors={colors}
                          />
                        );
                      }
                    }
                    return <ServiceItem key={service.id} service={service} colors={colors} />;
                  })}
                </div>
              );
            })}
          </div>

          {/* Ateliers & Conférences + Forfaits — bottom info card */}
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
              <h3 className="font-heading text-xl sm:text-2xl text-blue-700 font-bold mb-3 uppercase">
                Ateliers & Conférences
              </h3>
              <p className="font-body text-sm text-warm-brown mb-4">
                <strong>Activités créatives</strong> — ludiques et enrichissantes.<br />
                <strong>Conférences santé naturelle</strong> — équilibre du corps, gestion du stress, vitalité.
              </p>
              <p className="font-body text-xs text-blue-700 mb-3">
                Inscription sur Facebook, Instagram ou sur place
              </p>
              <div className="flex gap-2">
                <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white rounded-full">
                    <Facebook size={16} className="mr-1" /> Facebook
                  </Button>
                </a>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="border-pink-600 text-pink-600 hover:bg-pink-600 hover:text-white rounded-full">
                    <Instagram size={16} className="mr-1" /> Instagram
                  </Button>
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-rose-50 rounded-2xl p-6 border border-amber-200 text-center">
              <h3 className="font-heading text-xl sm:text-2xl text-amber-700 font-bold mb-3 uppercase">
                Forfaits Massage
              </h3>
              <p className="font-body text-sm text-warm-brown mb-4">
                Profitez de nos <strong>forfaits avantageux</strong> (3, 5 ou 10 séances) pour un suivi régulier à tarif préférentiel.
              </p>
              <Link to="/abonnements">
                <Button className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl px-6 py-3 font-body">
                  Voir les abonnements
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Legacy section — kept for backward compatibility, hidden */}
      {false && (
        <section className="py-8 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            
            {/* Colonne 1: LES MASSAGES */}
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl text-terracotta font-bold mb-4 uppercase tracking-wide">
                LES MASSAGES
              </h2>
              <div className="bg-terracotta/10 rounded-xl p-3 mb-4">
                <p className="font-body text-xs sm:text-sm text-warm-brown flex items-center gap-2">
                  <Users size={16} className="text-terracotta flex-shrink-0" />
                  <span>Option <strong>DUO</strong> disponible (prix x2)</span>
                </p>
              </div>
              {[].map(service => (
                <ServiceItem 
                  key={service.id} 
                  service={service} 
                  colors={CATEGORY_COLORS.massage}
                />
              ))}
            </div>

            {/* Colonne 2: BIEN-ÊTRE FÉMININ + NATUROPATHIE */}
            <div>
              <h2 className="font-heading text-xl sm:text-2xl text-[#748D78] font-bold mb-6 underline decoration-[#748D78] decoration-2 underline-offset-4">
                Bien-être féminin
              </h2>
              {(() => {
                const silhouetteServices = [].filter(s => s.name.includes('Silhouette'));
                const otherFemininServices = [].filter(s => !s.name.includes('Silhouette'));
                
                return (
                  <>
                    {otherFemininServices.map(service => (
                      <ServiceItem 
                        key={service.id} 
                        service={service} 
                        colors={CATEGORY_COLORS.feminin}
                      />
                    ))}
                    
                    {silhouetteServices.length > 0 && (
                      <GroupedServiceItem
                        groupName="Massage Silhouette"
                        services={silhouetteServices}
                        colors={CATEGORY_COLORS.feminin}
                      />
                    )}
                  </>
                );
              })()}

              {/* Naturopathie */}
              <h2 className="font-heading text-xl sm:text-2xl text-amber-600 font-bold mt-10 mb-6 uppercase text-center">
                CONSEILS EN<br />NATUROPATHIE
              </h2>
              <p className="font-body text-sm text-amber-500 text-center mb-4">(SUR RENDEZ-VOUS)</p>
              {[].map(service => (
                <ServiceItem 
                  key={service.id} 
                  service={service} 
                  colors={CATEGORY_COLORS.naturopathie}
                />
              ))}
            </div>

            {/* Colonne 3: ACTIVITÉS & CONFÉRENCES */}
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl text-blue-600 font-bold mb-6 uppercase tracking-wide text-right">
                ACTIVITÉS &<br />CONFÉRENCES
              </h2>
              
              {/* Activité créative */}
              <div className="bg-blue-50 rounded-2xl p-5 border border-blue-200 mb-4">
                <div className="flex flex-wrap items-baseline gap-2 mb-2">
                  <h3 className="font-heading text-lg text-blue-700">Activité créative</h3>
                  <span className="font-heading text-blue-600 underline">120min/25€</span>
                </div>
                <p className="font-body text-warm-brown text-sm leading-relaxed">
                  Offrez un moment créatif, ludique et enrichissant – découvrez les dates et les thèmes des ateliers sur nos pages Facebook et Instagram !
                </p>
              </div>

              {/* Conférence santé naturelle */}
              <div className="bg-blue-50 rounded-2xl p-5 border border-blue-200 mb-4">
                <div className="flex flex-wrap items-baseline gap-2 mb-2">
                  <h3 className="font-heading text-lg text-blue-700">Conférence sur la santé naturelle</h3>
                  <span className="font-heading text-blue-600 underline">120min/20€</span>
                </div>
                <p className="font-body text-warm-brown text-sm leading-relaxed">
                  Nous organisons des conférences sur la santé naturelle pour partager des connaissances utiles sur l'équilibre du corps, la gestion du stress et la vitalité au quotidien.
                </p>
              </div>

              {/* Inscription réseaux sociaux */}
              <div className="mt-4 p-4 bg-blue-100 rounded-xl border border-blue-200">
                <p className="font-body text-sm text-blue-800 text-center mb-3">
                  Inscription sur Facebook, Instagram ou sur place
                </p>
                <div className="flex justify-center gap-3">
                  <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white rounded-full">
                      <Facebook size={16} className="mr-1" /> Facebook
                    </Button>
                  </a>
                  <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="border-pink-600 text-pink-600 hover:bg-pink-600 hover:text-white rounded-full">
                      <Instagram size={16} className="mr-1" /> Instagram
                    </Button>
                  </a>
                </div>
              </div>

              {/* Forfaits */}
              <div className="mt-8">
                <h3 className="font-heading text-2xl text-blue-600 font-bold mb-4 uppercase">
                  LES FORFAITS
                </h3>
                <Link to="/abonnements">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-4 font-body">
                    Voir nos abonnements
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Info Section */}
      <section className="py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl text-warm-brown mb-6">Informations Importantes</h2>
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm">
            <ul className="font-body text-warm-brown-light space-y-4 text-left max-w-2xl mx-auto text-sm sm:text-base">
              <li className="flex items-start gap-3">
                <span className="text-terracotta text-xl">•</span>
                <span>Réservation sur <strong className="text-terracotta">Planity</strong> ou par téléphone au <a href="tel:0960421822" className="text-terracotta hover:underline font-semibold">09 60 42 18 22</a></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-sage text-xl">•</span>
                <span><strong className="text-sage-700">Tarif étudiant -10%</strong> sur présentation de la carte étudiante</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-500 text-xl">•</span>
                <span><strong>Paiement sur place</strong> le jour du rendez-vous</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-warm-brown text-xl">•</span>
                <span>Annulation gratuite jusqu'à 24h avant le rendez-vous</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Floating CTA - Réserver sur Planity */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-terracotta shadow-2xl z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="hidden sm:block text-left">
              <p className="font-heading text-lg text-warm-brown">Prêt à réserver ?</p>
              <p className="font-body text-sm text-warm-brown-light">Réservez sur Planity, payez sur place</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
              <Link to="/carte-cadeau" className="flex-1 sm:flex-none">
                <Button variant="outline" className="w-full sm:w-auto border-amber-500 text-amber-600 hover:bg-amber-50 rounded-full px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base">
                  🎁 Offrir
                </Button>
              </Link>
              <a href={PLANITY_URL} target="_blank" rel="noopener noreferrer" className="flex-1 sm:flex-none">
                <Button 
                  className="w-full sm:w-auto bg-terracotta hover:bg-terracotta-dark text-white rounded-full px-4 sm:px-8 py-2.5 sm:py-4 font-body text-sm sm:text-lg"
                  data-testid="reserve-planity-btn"
                >
                  <Calendar className="mr-1 sm:mr-2" size={16} />
                  <span className="sm:hidden">Réserver</span>
                  <span className="hidden sm:inline">Réserver sur Planity</span>
                  <ExternalLink className="ml-1 sm:ml-2 hidden sm:inline" size={16} />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesSimple;
